/**
 * Calculates the Levenshtein distance between two strings, which is the minimum number of
 * insertions, deletions, and updates required to transform one string into the other.
 * This function only considers insertions, and ignores deletions and updates.
 *
 * When A deletion does occur it can't match the next character in the string, so it will
 * be counted as an insertion in the next iteration. This will result in a higher distance (normally the count of the strings together)
 *
 * To simplify these cases we will return -1 when this happens.
 *
 * @param str1
 * @param str2
 * @returns
 */
export function levenshteinDistanceInsertionsOnly(
  str1: string,
  str2: string
): number {
  const dp: number[][] = [];

  const m = str1.length;
  const n = str2.length;

  // Initialize the 2D dp array
  for (let i = 0; i <= m; i++) {
    dp[i] = [];
    for (let j = 0; j <= n; j++) {
      // If the first string is empty, the number of operations required
      // is equal to the number of characters in the second string.
      // Will set the value to the column number when in the first row
      if (i === 0) {
        dp[i][j] = j;
      }
      // If the second string is empty, the number of operations required
      // is equal to the number of characters in the first string.
      // Will set the value to the row number when in the first column
      else if (j === 0) {
        dp[i][j] = i * 150;
      }
      // If the characters at the current positions in both strings are the same,
      // the number of operations required is the same as the number of operations
      // required for the substrings without these characters.
      else if (str1[i - 1] === str2[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1];
      }
      // If the characters at the current positions in both strings are different,
      // the number of operations required is equal to 1 (for an insertion)
      // plus the number of operations required to transform the rest of the strings.
      else {
        dp[i][j] = Math.min(
          dp[i][j - 1] + 1,
          dp[i - 1][j] + 150,
          dp[i - 1][j - 1] + 150
        );
      }
    }
  }

  // Return the final value in the dp array, which represents the number of
  // insertions required to transform the first string into the second string.
  // return -1 if the distance is the sum of the lengths of the strings - this means a deletion or update was found
  return dp[m][n];
}
